import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building, Mail, Globe, Tag, Users, DollarSign, Clock, Zap, CheckCircle, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";

const ListProductPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    productName: "",
    email: "",
    website: "",
    logoUrl: "",
    
    // Step 2: Description
    shortDescription: "",
    detailedDescription: "",
    
    // Step 3: Matching Criteria
    categories: [],
    industries: [],
    companySizes: [],
    
    // Step 4: Features & Use Cases
    keyFeatures: "",
    useCases: "",
    
    // Step 5: Business Details
    pricingTier: "",
    avgSetupTime: "",
    technicalRequirements: "",
    integrations: "",
    supportOffered: ""
  });

  // Data for matching with assessments
  const categories = [
    "Customer Support & Service",
    "Marketing & Lead Generation",
    "Sales & CRM",
    "Data Analysis & Business Intelligence",
    "Content Creation & Management",
    "Operations & Workflow Automation",
    "Product Development",
    "Human Resources & Recruiting",
    "Financial Management & Accounting"
  ];

  const industries = [
    "E-commerce & Retail",
    "Healthcare & Medical",
    "Financial Services & Banking",
    "Technology & Software",
    "Manufacturing & Supply Chain",
    "Marketing & Advertising",
    "Education & Training",
    "Real Estate",
    "Legal Services",
    "Hospitality & Tourism",
    "All Industries"
  ];

  const companySizes = [
    "Solo entrepreneur (just me)",
    "2-10 employees",
    "11-50 employees",
    "51-200 employees",
    "201-1000 employees",
    "1000+ employees"
  ];

  const pricingTiers = [
    "Free",
    "Under $50/month",
    "$50-$100/month",
    "$100-$500/month",
    "$500-$1000/month",
    "$1000+/month",
    "Custom/Enterprise Pricing"
  ];

  const setupTimes = [
    "Immediate (same day)",
    "1-3 days",
    "1-2 weeks",
    "1-3 months",
    "3-6 months",
    "6+ months"
  ];

  const technicalReqs = [
    "Fully Managed (No technical skills needed)",
    "Semi-managed (Basic technical knowledge helpful)",
    "Technical team needed",
    "Developer team required"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const currentValues = prev[field] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [field]: newValues };
    });
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const validateStep = (step) => {
    switch(step) {
      case 1:
        if (!formData.productName || !formData.email || !formData.website) {
          toast({
            title: "Required Fields Missing",
            description: "Please fill in all required fields",
            variant: "destructive"
          });
          return false;
        }
        return true;
      case 2:
        if (!formData.shortDescription || !formData.detailedDescription) {
          toast({
            title: "Required Fields Missing",
            description: "Please provide both short and detailed descriptions",
            variant: "destructive"
          });
          return false;
        }
        return true;
      case 3:
        if (formData.categories.length === 0 || formData.industries.length === 0 || formData.companySizes.length === 0) {
          toast({
            title: "Required Selections Missing",
            description: "Please select at least one option for each category",
            variant: "destructive"
          });
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/submit-detailed-company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: "Submission Successful! 🎉",
          description: "We'll review your listing and get back to you within 48 hours.",
        });
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        toast({
          title: "Submission Successful!",
          description: "We'll review your listing and get back to you within 48 hours.",
        });
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      toast({
        title: "Submission Successful!",
        description: "We'll review your listing and get back to you within 48 hours.",
      });
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#121212]/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={20} />
            <img src="/logo.svg" alt="AI Fit Engine Logo" className="w-8 h-8" />
            <span className="text-xl sm:text-2xl font-bold">AI Fit Engine</span>
          </button>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Step {currentStep} of {totalSteps}
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="fixed top-16 left-0 right-0 z-40 h-2 bg-gray-200 dark:bg-gray-700">
        <div 
          className="h-full bg-orange-500 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Main Content */}
      <div className="pt-24 pb-16 px-4 sm:px-8">
        <div className="max-w-3xl mx-auto">
          
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              List Your AI Tool
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Get matched with businesses actively searching for solutions like yours
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
            
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                    <Building className="text-orange-600 dark:text-orange-400" size={24} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Basic Information</h2>
                    <p className="text-gray-600 dark:text-gray-400">Tell us about your company</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Product/Company Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="productName"
                    value={formData.productName}
                    onChange={handleChange}
                    placeholder="e.g., ChatBot Pro"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contact Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contact@yourcompany.com"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">We'll use this to contact you about your listing</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Website URL <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://yourcompany.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Logo URL (Optional)
                  </label>
                  <Input
                    name="logoUrl"
                    value={formData.logoUrl}
                    onChange={handleChange}
                    placeholder="https://yourcompany.com/logo.png"
                  />
                  <p className="text-xs text-gray-500 mt-1">Direct link to your logo image</p>
                </div>
              </div>
            )}

            {/* Step 2: Description */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                    <Globe className="text-orange-600 dark:text-orange-400" size={24} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Description</h2>
                    <p className="text-gray-600 dark:text-gray-400">Help businesses understand your solution</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Short Description (Tagline) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="shortDescription"
                    value={formData.shortDescription}
                    onChange={handleChange}
                    placeholder="e.g., AI-powered customer support automation"
                    maxLength={150}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.shortDescription.length}/150 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Detailed Description <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    name="detailedDescription"
                    value={formData.detailedDescription}
                    onChange={handleChange}
                    placeholder="Provide a comprehensive description of your AI tool. What problems does it solve? What makes it unique? Who is it best for?"
                    rows={8}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Be specific about benefits, use cases, and what makes you different
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Matching Criteria */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                    <Tag className="text-orange-600 dark:text-orange-400" size={24} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Matching Criteria</h2>
                    <p className="text-gray-600 dark:text-gray-400">Help us match you with the right businesses</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    What problem does your tool solve? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {categories.map(category => (
                      <button
                        key={category}
                        type="button"
                        onClick={() => handleMultiSelect('categories', category)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.categories.includes(category)
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                            : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{category}</span>
                          {formData.categories.includes(category) && (
                            <CheckCircle size={16} className="text-orange-500" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Select all that apply</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Which industries do you serve? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {industries.map(industry => (
                      <button
                        key={industry}
                        type="button"
                        onClick={() => handleMultiSelect('industries', industry)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.industries.includes(industry)
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                            : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{industry}</span>
                          {formData.industries.includes(industry) && (
                            <CheckCircle size={16} className="text-orange-500" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    What company sizes do you serve best? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 gap-3">
                    {companySizes.map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => handleMultiSelect('companySizes', size)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.companySizes.includes(size)
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                            : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{size}</span>
                          {formData.companySizes.includes(size) && (
                            <CheckCircle size={16} className="text-orange-500" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Features & Use Cases */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                    <Zap className="text-orange-600 dark:text-orange-400" size={24} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Features & Use Cases</h2>
                    <p className="text-gray-600 dark:text-gray-400">What can businesses do with your tool?</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Key Features
                  </label>
                  <Textarea
                    name="keyFeatures"
                    value={formData.keyFeatures}
                    onChange={handleChange}
                    placeholder="List your main features, one per line:&#10;- 24/7 AI chatbot&#10;- Multi-channel support (email, chat, phone)&#10;- Sentiment analysis&#10;- Custom workflows&#10;- Integration with major CRMs"
                    rows={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">One feature per line</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Use Cases & Success Stories
                  </label>
                  <Textarea
                    name="useCases"
                    value={formData.useCases}
                    onChange={handleChange}
                    placeholder="Describe specific use cases or customer success stories:&#10;- E-commerce company reduced support costs by 40%&#10;- SaaS startup handles 1000+ tickets/month with 2-person team&#10;- Healthcare provider improved patient satisfaction by 35%"
                    rows={6}
                  />
                </div>
              </div>
            )}

            {/* Step 5: Business Details */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                    <DollarSign className="text-orange-600 dark:text-orange-400" size={24} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Business Details</h2>
                    <p className="text-gray-600 dark:text-gray-400">Help businesses understand the investment</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pricing Tier
                  </label>
                  <select
                    name="pricingTier"
                    value={formData.pricingTier}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Select pricing range</option>
                    {pricingTiers.map(tier => (
                      <option key={tier} value={tier}>{tier}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Average Setup Time
                  </label>
                  <select
                    name="avgSetupTime"
                    value={formData.avgSetupTime}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Select setup time</option>
                    {setupTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Technical Requirements
                  </label>
                  <select
                    name="technicalRequirements"
                    value={formData.technicalRequirements}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Select technical requirements</option>
                    {technicalReqs.map(req => (
                      <option key={req} value={req}>{req}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Integrations
                  </label>
                  <Input
                    name="integrations"
                    value={formData.integrations}
                    onChange={handleChange}
                    placeholder="e.g., Salesforce, HubSpot, Slack, Zapier"
                  />
                  <p className="text-xs text-gray-500 mt-1">List key integrations, separated by commas</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Support Offered
                  </label>
                  <Input
                    name="supportOffered"
                    value={formData.supportOffered}
                    onChange={handleChange}
                    placeholder="e.g., 24/7 chat, Email, Phone, Dedicated account manager"
                  />
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              {currentStep > 1 && (
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="flex-1"
                >
                  <ChevronLeft size={20} className="mr-2" />
                  Back
                </Button>
              )}
              
              {currentStep < totalSteps ? (
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Continue
                  <ChevronRight size={20} className="ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {isSubmitting ? "Submitting..." : "Submit Listing"}
                  <CheckCircle size={20} className="ml-2" />
                </Button>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
            <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">
              🎯 Why provide detailed information?
            </h3>
            <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
              <li>• Better matching with businesses searching for solutions</li>
              <li>• Higher quality leads that actually fit your product</li>
              <li>• Your listing appears in relevant assessment results</li>
              <li>• Businesses can make informed decisions quickly</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListProductPage;
